import React, { useMemo } from 'react';
import type { TdsOutVarItem } from '../../../types/testDataCharts.types';

interface TestDataBarChartProps {
  tdsOutVarList: TdsOutVarItem[];
  valueName: string;
  outsideVarName: string;
}

const CHART_W = 600;
const CHART_H = 260;
const PAD_L = 65;
const PAD_R = 15;
const PAD_T = 15;
const PAD_B = 60;
const PLOT_W = CHART_W - PAD_L - PAD_R;
const PLOT_H = CHART_H - PAD_T - PAD_B;

const BAR_COLORS = ['#0070B9', '#E8560A', '#2ECC71', '#9B59B6', '#F39C12', '#1ABC9C', '#E74C3C', '#3498DB'];

/** Rounds value to 4 decimal places, matching legacy formatValueForDisplay. */
function formatValue(value: number): string {
  return (Math.round(value * 10000) / 10000).toFixed(4);
}

const TestDataBarChart: React.FC<TestDataBarChartProps> = ({ tdsOutVarList, valueName, outsideVarName }) => {
  const { bars, maxVal, yTicks } = useMemo(() => {
    const computed = tdsOutVarList.map((item) => ({
      label: item.outsideVarValue === null || item.outsideVarValue === '' ? '--' : item.outsideVarValue,
      value: item.sumDenominator === 0
        ? 0
        : Math.round((item.sumNumerator / item.sumDenominator) * 10000) / 10000,
    }));

    const max = Math.max(...computed.map((b) => b.value), 0);
    const topVal = max * 1.15 || 1;
    const ticks = Array.from({ length: 5 }, (_, i) => (topVal * i) / 4);

    return { bars: computed, maxVal: topVal, yTicks: ticks };
  }, [tdsOutVarList]);

  if (tdsOutVarList.length === 0) return null;

  const barWidth = Math.min(60, (PLOT_W / bars.length) * 0.6);
  const barSpacing = PLOT_W / bars.length;
  const yPos = (value: number): number => PLOT_H - (value / maxVal) * PLOT_H;
  const barHeight = (value: number): number => (value / maxVal) * PLOT_H;

  return (
    <div className="overflow-x-auto w-full">
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        width="100%"
        aria-label={`${valueName} by ${outsideVarName} - Bar Chart`}
      >
        <title>{valueName} by {outsideVarName} - Bar Chart</title>

        {/* Chart title */}
        <text
          x={CHART_W / 2}
          y="12"
          textAnchor="middle"
          fontSize="12"
          fill="#374151"
          fontWeight="600"
        >
          {valueName} by {outsideVarName}
        </text>

        <g transform={`translate(${PAD_L}, ${PAD_T})`}>
          {/* Grid lines and Y axis ticks */}
          {yTicks.map((tick) => (
            <g key={tick} transform={`translate(0, ${yPos(tick)})`}>
              <line x1="-5" y1="0" x2={PLOT_W} y2="0" stroke="#e5e7eb" strokeWidth="1" />
              <line x1="-5" y1="0" x2="0" y2="0" stroke="#6b7280" strokeWidth="1" />
              <text x="-8" y="4" textAnchor="end" fontSize="10" fill="#6b7280">
                {formatValue(tick)}
              </text>
            </g>
          ))}

          {/* Y axis line */}
          <line x1="0" y1="0" x2="0" y2={PLOT_H} stroke="#6b7280" strokeWidth="1.5" />

          {/* X axis */}
          <line x1="0" y1={PLOT_H} x2={PLOT_W} y2={PLOT_H} stroke="#6b7280" strokeWidth="1.5" />

          {/* Bars */}
          {bars.map((bar, i) => {
            const cx = barSpacing * i + barSpacing / 2;
            const bh = barHeight(bar.value);
            return (
              <g key={bar.label}>
                <rect
                  x={cx - barWidth / 2}
                  y={yPos(bar.value)}
                  width={barWidth}
                  height={bh}
                  fill={BAR_COLORS[i % BAR_COLORS.length]}
                >
                  <title>{`${bar.label}: ${formatValue(bar.value)}`}</title>
                </rect>
                {/* X axis label */}
                <text
                  x={cx}
                  y={PLOT_H + 15}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#6b7280"
                >
                  {bar.label.length > 10 ? `${bar.label.substring(0, 10)}…` : bar.label}
                </text>
              </g>
            );
          })}

          {/* Y axis label */}
          <text
            transform={`translate(${-PAD_L + 12}, ${PLOT_H / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="11"
            fill="#374151"
          >
            {valueName}
          </text>
        </g>
      </svg>
    </div>
  );
};

export default TestDataBarChart;
